import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import axios from 'axios';
import Cookies from 'js-cookie';
import { generateToken } from '../notifications/firebase';
import FFBindingModal from '../components/FFBindingModal';
import NotificationPermissionModal from '../components/NotificationPermissionModal';

// Lazy Load Pages
const AuthComponent = lazy(() => import('../pages/auth/Login'));
const Home = lazy(() => import('../pages/user/Home'));
const MyMatches = lazy(() => import('../pages/user/MyMatches'));
const ViewDetails = lazy(() => import('../pages/user/ViewDetails'));
const Wallet = lazy(() => import('../pages/user/Wallet'));
const Leaderboard = lazy(() => import('../pages/user/Leaderboard'));
const Profile = lazy(() => import('../pages/user/Profile'));
const Notifications = lazy(() => import('../pages/user/Notifications'));
const JoinTournament = lazy(() => import('../pages/user/JoinTournament'));
const LandingPage = lazy(() => import('../pages/user/LandingPage'));
const ErrorPage = lazy(() => import('../pages/ErrorPage'));
import Chatbot from '../components/Chatbot';

// Simple loading fallback
const PageLoader = () => (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center">
        <div className="w-12 h-12 border-4 border-yellow-500/20 border-t-yellow-500 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-400 animate-pulse">Loading experience...</p>
    </div>
);


import { useAppState } from '../context/AppStateContext';

const UserLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { restoreLastRoute } = useAppState();
    const [currentView, setCurrentView] = useState('landing');
    const [errorState, setErrorState] = useState({ code: 404, message: '' });

    // ✅ PERSISTENT LOGIN INITIALIZATION
    const [userProfile, setUserProfile] = useState(() => {
        const savedUser = localStorage.getItem('ff_arena_user_session');
        return savedUser ? JSON.parse(savedUser) : null;
    });

    const [isAuthenticating, setIsAuthenticating] = useState(() => {
        // If we have a saved user, don't block the initial render
        return !localStorage.getItem('ff_arena_user_session');
    });



    const [showFFModal, setShowFFModal] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState(false);

    // ✅ SESSION PRE-FLIGHT (Prevent Flicker)
    React.useEffect(() => {
        const validateSession = async () => {
            const path = location.pathname;
            const protectedRoutes = ['/home', '/my-matches', '/view-details', '/wallet', '/leaderboard', '/profile', '/notifications', '/join-tournament'];

            // Only validate if we are trying to access a protected route or root with active session
            const hasLocalSession = !!localStorage.getItem('ff_arena_user_session');
            const isProtectedRoute = protectedRoutes.some(route => path.startsWith(route)) || (path === '/' && hasLocalSession);

            if (isProtectedRoute) {
                try {
                    const response = await axios.get(`${backendUrl}/api/profile`);
                    const user = response.data.user;
                    setUserProfile(user);

                    // ✅ SYNC LOCALSTORAGE
                    if (user && !user.roles.includes('admin')) {
                        localStorage.setItem('ff_arena_user_session', JSON.stringify(user));
                    }

                    // If user is verified and not an admin, generate notification token
                    if (user && !user.roles.includes('admin') && user.isVerified) {
                        if (Notification.permission === 'granted') {
                            generateToken();
                        } else if (Notification.permission === 'default') {
                            // Show custom permission modal
                            setTimeout(() => setShowNotificationModal(true), 3000); // Delay slightly for better UX
                        }

                        // ✅ FF ACCOUNT BINDING CHECK (Priority: Verification Notification -> Auto-show on Join Tournament)
                        const showForNotification =
                            (user.ffStatus === 'verified' && !user.ffVerificationSeen) ||
                            (user.ffStatus === 'rejected');

                        if (showForNotification) {
                            setShowFFModal(true);
                        } else if (user.ffStatus !== 'verified' && path === '/join-tournament') {
                            setShowFFModal(true);
                        }
                    }

                    // If user is an admin, they should not be in the user flow
                    if (user && user.roles.includes('admin')) {
                        localStorage.removeItem('ff_arena_user_session'); // Security: Don't persist admin
                        localStorage.removeItem('ff_arena_token');
                        setIsAuthenticating(false);
                        navigate('/admin/dashboard');
                        return;
                    }

                    setIsAuthenticating(false);
                    // Only restore if we are at root or home, otherwise respect current URL
                    if (path === '/' || path === '/home') {
                        restoreLastRoute(true);
                    }
                } catch (error) {
                    if (error.response?.status === 401) {
                        // ❌ SESSION INVALID - genuine 401 from server
                        localStorage.removeItem('ff_arena_user_session');
                        localStorage.removeItem('ff_arena_token');
                        delete axios.defaults.headers.common['Authorization'];
                        setUserProfile(null);
                        setIsAuthenticating(false);
                        if (path !== '/' && path !== '/landing') navigate('/auth');
                    } else {
                        // ⚠️ Network / cookie-blocked error (common on mobile Safari)
                        // Fall back to localStorage session so user isn't kicked out
                        const savedUser = localStorage.getItem('ff_arena_user_session');
                        if (savedUser) {
                            try {
                                setUserProfile(JSON.parse(savedUser));
                            } catch (_) {
                                // Corrupted localStorage data — ignore
                            }
                        } else if (path !== '/' && path !== '/landing' && path !== '/auth') {
                            navigate('/auth');
                        }
                        setIsAuthenticating(false);
                    }
                }
            } else {
                setIsAuthenticating(false);
            }
        };

        validateSession();

        // ✅ EXPOSE REFRESH GLOBALLY
        window.refreshUserProfile = validateSession;
    }, [navigate, location.pathname]);

    // ✅ GLOBAL EXPOSURE FOR FF MODAL
    React.useEffect(() => {
        window.showFFBindingGlobal = () => setShowFFModal(true);
        return () => {
            delete window.showFFBindingGlobal;
        };
    }, []);

    // URL change par view update karo
    React.useEffect(() => {
        const path = location.pathname;

        // Define valid routes
        const validAppRoutes = ['/', '/home', '/my-matches', '/view-details', '/wallet', '/leaderboard', '/profile', '/notifications', '/join-tournament'];

        if (path === '/home') {
            setCurrentView('app');
        } else if (path === '/auth') {
            setCurrentView('auth');
        } else if (path === '/reset-password') {
            setCurrentView('reset-password');
        } else if (path === '/landing' || path === '/') {
            setCurrentView('landing');
        } else if (validAppRoutes.includes(path)) {
            setCurrentView('app');
        } else {
            // Invalid route - show error
            setErrorState({
                code: 404,
                message: `The route "${location.pathname}" was not found.`
            });
            setCurrentView('error');
        }
    }, [location.pathname]);

    // Function to handle API errors from any component
    const handleApiError = (error) => {
        const statusCode = error.response?.status || 500;
        const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';

        setErrorState({
            code: statusCode,
            message: errorMessage
        });
        setCurrentView('error');
    };

    const handleGetStarted = () => {
        setCurrentView('auth');
        navigate('/auth');
    };

    const handleAuthSuccess = () => {
        setCurrentView('app');
        navigate('/home');
    };



    const handleGoHome = () => {
        setCurrentView('app');
        navigate('/home');
    };

    const handleGoLanding = () => {
        setCurrentView('landing');
        navigate('/landing');
    };

    const handleResetPassword = () => {
        setCurrentView('resetPassword')
        navigate('/reset-password')
    }

    // Clear error and go back to previous view
    const handleClearError = () => {
        setCurrentView('app');
        navigate('/home');
    };

    if (isAuthenticating) {
        return <PageLoader />;
    }

    return (
        <Suspense fallback={<PageLoader />}>
            <div>
                {currentView === 'landing' && (
                    <LandingPage onGetStarted={handleGetStarted} />
                )}

                {currentView === 'auth' && (
                    <AuthComponent
                        onAuthSuccess={handleAuthSuccess}
                        onApiError={handleApiError} // Pass error handler to auth component
                    />
                )}

                {currentView === 'reset-password' && (
                    <PasswordReset
                        onApiError={handleApiError} // Pass error handler
                    />
                )}

                {currentView === 'app' && (
                    <>
                        <Navbar>
                            <Routes>
                                <Route path="/" element={<Home onApiError={handleApiError} />} />
                                <Route path="/home" element={<Home onApiError={handleApiError} />} />
                                <Route path="/my-matches" element={<MyMatches onApiError={handleApiError} />} />
                                <Route path="/view-details" element={<ViewDetails onApiError={handleApiError} />} />
                                <Route path="/wallet" element=
                                    {<Wallet onApiError={handleApiError} />} />
                                <Route path="/leaderboard" element={<Leaderboard onApiError={handleApiError} />} />
                                <Route path="/profile" element={<Profile onApiError={handleApiError} />} />
                                <Route path="/notifications" element={<Notifications onApiError={handleApiError} />} />
                                <Route path="/join-tournament" element={<JoinTournament onApiError={handleApiError} />} />

                                {/* Catch all undefined routes in app section */}
                                <Route path="*" element={
                                    <ErrorPage
                                        errorCode={404}
                                        errorMessage="The page you're looking for doesn't exist in the app."
                                        onGoHome={handleGoHome}
                                        onGoLanding={handleGoLanding}
                                    />
                                } />
                            </Routes>
                        </Navbar>
                        {userProfile && <Chatbot />}
                    </>
                )}
                {currentView === 'error' && (
                    <ErrorPage
                        errorCode={errorState.code}
                        errorMessage={errorState.message}
                        onGoHome={handleGoHome}
                        onGoLanding={handleGoLanding}
                        onRetry={handleClearError}
                    />
                )}

                <FFBindingModal
                    isOpen={showFFModal}
                    onClose={() => setShowFFModal(false)}
                    userData={userProfile}
                    onStatusUpdate={(updatedUser) => {
                        setUserProfile(updatedUser);
                        // ✅ Sync localStorage with the updated user data (including new username/ffStatus)
                        localStorage.setItem('ff_arena_user_session', JSON.stringify(updatedUser));

                        if (updatedUser.ffStatus === 'under_verification') {
                            // Keep modal open to show verification page
                        } else {
                            setShowFFModal(false);
                        }
                    }}
                />

                <NotificationPermissionModal
                    isOpen={showNotificationModal}
                    onClose={() => setShowNotificationModal(false)}
                    onAllow={() => setShowNotificationModal(false)}
                />
            </div>
        </Suspense>
    );
};

export default UserLayout;
